import type {
  AuthorStoryResponse,
  ChapterResponse,
  ChaptersResponse,
  ConnectionResponse,
  CreateChapterBody,
  CreateConnectionBody,
  CreateExitBody,
  CreateFlagBody,
  CreateItemBody,
  CreateSceneBody,
  CreateStoryBody,
  ExitResponse,
  FlagResponse,
  FlagsResponse,
  ItemResponse,
  SceneResponse,
  StoryListResponse,
  StoryResponse,
  UpdateChapterBody,
  UpdateConnectionBody,
  UpdateExitBody,
  UpdateFlagBody,
  UpdateItemBody,
  UpdateSceneBody,
  UpdateStoryBody,
  CreateSessionBody,
  EditorSelection,
  EditorStreamEvent,
  EditorThreadResponse,
  PlayStreamEvent,
  SessionResponse,
  SessionsResponse,
  SessionSummaryResponse,
  StoryGraphResponse,
  PlayerMapResponse,
  ModelsResponse,
  UpdateSessionBody,
  UploadResponse,
  CreateVersionBody,
  UpdateVersionBody,
  VersionResponse,
  VersionsResponse,
  AnswerCardBody,
  AnswerCardResponse,
  CreateTargetWordBody,
  CreateVocabWordBody,
  ProfileResponse,
  TargetWordResponse,
  TargetWordsResponse,
  UpdateTargetWordBody,
  UpdateVocabWordBody,
  VocabWordResponse,
  VocabWordsResponse,
} from "@storyai/shared";
import { createSseParser } from "./sse";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiUser {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; issues?: { path: string; message: string }[] };
    throw new ApiError(res.status, d.error ?? `Request failed (${res.status})`, d.issues);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

export const authApi = {
  me: () => api.get<{ user: ApiUser }>("/api/auth/me"),
  login: (email: string, password: string) =>
    api.post<{ user: ApiUser }>("/api/auth/login", { email, password }),
  signup: (email: string, password: string, displayName: string) =>
    api.post<{ user: ApiUser }>("/api/auth/signup", { email, password, displayName }),
  logout: () => api.post<void>("/api/auth/logout"),
};

// ---- Authoring API (docs/api.md) -------------------------------------------

const storyPath = (storyId: string) => `/api/stories/${storyId}`;
const chapterPath = (storyId: string, chapterId: string) =>
  `${storyPath(storyId)}/chapters/${chapterId}`;
const scenePath = (storyId: string, chapterId: string, sceneId: string) =>
  `${chapterPath(storyId, chapterId)}/scenes/${sceneId}`;

export const storiesApi = {
  listPublic: () => api.get<StoryListResponse>("/api/stories"),
  listMine: () => api.get<StoryListResponse>("/api/stories/mine"),
  create: (body: CreateStoryBody = {}) => api.post<AuthorStoryResponse>("/api/stories", body),
  get: (storyId: string) => api.get<StoryResponse>(storyPath(storyId)),
  update: (storyId: string, body: UpdateStoryBody) =>
    api.patch<AuthorStoryResponse>(storyPath(storyId), body),
  remove: (storyId: string) => api.delete<void>(storyPath(storyId)),
};

export const chaptersApi = {
  create: (storyId: string, body: CreateChapterBody = {}) =>
    api.post<ChapterResponse>(`${storyPath(storyId)}/chapters`, body),
  get: (storyId: string, chapterId: string) =>
    api.get<ChapterResponse>(chapterPath(storyId, chapterId)),
  update: (storyId: string, chapterId: string, body: UpdateChapterBody) =>
    api.patch<ChapterResponse>(chapterPath(storyId, chapterId), body),
  remove: (storyId: string, chapterId: string) =>
    api.delete<void>(chapterPath(storyId, chapterId)),
  reorder: (storyId: string, chapterIds: string[]) =>
    api.put<ChaptersResponse>(`${storyPath(storyId)}/chapters/reorder`, { chapterIds }),
};

export const scenesApi = {
  create: (storyId: string, chapterId: string, body: CreateSceneBody) =>
    api.post<SceneResponse>(`${chapterPath(storyId, chapterId)}/scenes`, body),
  get: (storyId: string, chapterId: string, sceneId: string) =>
    api.get<SceneResponse>(scenePath(storyId, chapterId, sceneId)),
  update: (storyId: string, chapterId: string, sceneId: string, body: UpdateSceneBody) =>
    api.patch<SceneResponse>(scenePath(storyId, chapterId, sceneId), body),
  remove: (storyId: string, chapterId: string, sceneId: string) =>
    api.delete<void>(scenePath(storyId, chapterId, sceneId)),
};

export const itemsApi = {
  create: (storyId: string, chapterId: string, sceneId: string, body: CreateItemBody) =>
    api.post<ItemResponse>(`${scenePath(storyId, chapterId, sceneId)}/items`, body),
  update: (
    storyId: string,
    chapterId: string,
    sceneId: string,
    itemId: string,
    body: UpdateItemBody,
  ) => api.patch<ItemResponse>(`${scenePath(storyId, chapterId, sceneId)}/items/${itemId}`, body),
  remove: (storyId: string, chapterId: string, sceneId: string, itemId: string) =>
    api.delete<void>(`${scenePath(storyId, chapterId, sceneId)}/items/${itemId}`),
};

export const connectionsApi = {
  create: (storyId: string, chapterId: string, sceneId: string, body: CreateConnectionBody) =>
    api.post<ConnectionResponse>(`${scenePath(storyId, chapterId, sceneId)}/connections`, body),
  update: (
    storyId: string,
    chapterId: string,
    sceneId: string,
    connectionId: string,
    body: UpdateConnectionBody,
  ) =>
    api.patch<ConnectionResponse>(
      `${scenePath(storyId, chapterId, sceneId)}/connections/${connectionId}`,
      body,
    ),
  remove: (storyId: string, chapterId: string, sceneId: string, connectionId: string) =>
    api.delete<void>(`${scenePath(storyId, chapterId, sceneId)}/connections/${connectionId}`),
};

export const flagsApi = {
  list: (storyId: string) => api.get<FlagsResponse>(`${storyPath(storyId)}/flags`),
  create: (storyId: string, body: CreateFlagBody) =>
    api.post<FlagResponse>(`${storyPath(storyId)}/flags`, body),
  update: (storyId: string, flagId: string, body: UpdateFlagBody) =>
    api.patch<FlagResponse>(`${storyPath(storyId)}/flags/${flagId}`, body),
  remove: (storyId: string, flagId: string) =>
    api.delete<void>(`${storyPath(storyId)}/flags/${flagId}`),
};

export const exitsApi = {
  create: (storyId: string, chapterId: string, body: CreateExitBody) =>
    api.post<ExitResponse>(`${chapterPath(storyId, chapterId)}/exits`, body),
  update: (storyId: string, chapterId: string, exitId: string, body: UpdateExitBody) =>
    api.patch<ExitResponse>(`${chapterPath(storyId, chapterId)}/exits/${exitId}`, body),
  remove: (storyId: string, chapterId: string, exitId: string) =>
    api.delete<void>(`${chapterPath(storyId, chapterId)}/exits/${exitId}`),
};

export const versionsApi = {
  list: (baseId: string) => api.get<VersionsResponse>(`${storyPath(baseId)}/versions`),
  create: (baseId: string, body: CreateVersionBody) =>
    api.post<VersionResponse>(`${storyPath(baseId)}/versions`, body),
  update: (baseId: string, versionId: string, body: UpdateVersionBody) =>
    api.patch<VersionResponse>(`${storyPath(baseId)}/versions/${versionId}`, body),
  retry: (baseId: string, versionId: string) =>
    api.post<VersionResponse>(`${storyPath(baseId)}/versions/${versionId}/retry`),
  remove: (baseId: string, versionId: string) =>
    api.delete<void>(`${storyPath(baseId)}/versions/${versionId}`),
};

export const graphApi = {
  get: (storyId: string) => api.get<StoryGraphResponse>(`${storyPath(storyId)}/graph`),
};

// ---- Sessions / play (docs/api.md "Sessions", packages/shared/src/play.ts) --

const sessionPath = (storyId: string, sessionId: string) =>
  `${storyPath(storyId)}/sessions/${sessionId}`;

/** Selectable LLMs. The server never returns the OpenRouter slug. */
export const modelsApi = {
  list: () => api.get<ModelsResponse>("/api/models"),
};

export const sessionsApi = {
  list: () => api.get<SessionsResponse>("/api/sessions"),
  listForStory: (storyId: string) => api.get<SessionsResponse>(`${storyPath(storyId)}/sessions`),
  create: (storyId: string, body: CreateSessionBody = {}) =>
    api.post<SessionResponse>(`${storyPath(storyId)}/sessions`, body),
  get: (storyId: string, sessionId: string) =>
    api.get<SessionResponse>(sessionPath(storyId, sessionId)),
  /** Rename and/or change the game master model (applies next turn). */
  update: (storyId: string, sessionId: string, body: UpdateSessionBody) =>
    api.patch<SessionSummaryResponse>(sessionPath(storyId, sessionId), body),
  rename: (storyId: string, sessionId: string, name: string) =>
    api.patch<SessionSummaryResponse>(sessionPath(storyId, sessionId), { name }),
  remove: (storyId: string, sessionId: string) =>
    api.delete<void>(sessionPath(storyId, sessionId)),
  map: (storyId: string, sessionId: string) =>
    api.get<PlayerMapResponse>(`${sessionPath(storyId, sessionId)}/map`),

  /**
   * Sends a player message and streams the GM's turn back as PlayStreamEvents.
   * Resolves when the stream ends; rejects with ApiError if the request is
   * refused before streaming (409 busy/finished, 503 no model key, …).
   */
  async sendMessage(
    storyId: string,
    sessionId: string,
    content: string,
    onEvent: (event: PlayStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    await streamPost<PlayStreamEvent>(`${sessionPath(storyId, sessionId)}/messages`, { content }, onEvent, signal);
  },
};

/**
 * POST JSON and read a `text/event-stream` response, dispatching each frame as
 * `{...data, type: event}`. Throws ApiError from the JSON body when not ok.
 */
async function streamPost<E>(
  path: string,
  body: unknown,
  onEvent: (event: E) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    credentials: "same-origin",
    signal,
  });

  if (!res.ok) {
    let data: { error?: string; issues?: { path: string; message: string }[] } = {};
    try {
      data = (await res.json()) as typeof data;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.issues);
  }
  if (!res.body) throw new ApiError(res.status, "The server sent no response body");

  const parser = createSseParser();
  const decoder = new TextDecoder();
  const reader = res.body.getReader();

  const dispatch = (frames: { event: string; data: string }[]) => {
    for (const f of frames) {
      if (!f.data) continue;
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(f.data) as Record<string, unknown>;
      } catch {
        continue;
      }
      onEvent({ ...payload, type: f.event } as E);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    dispatch(parser.push(decoder.decode(value, { stream: true })));
  }
  dispatch(parser.push(decoder.decode()));
  dispatch(parser.flush());
}

// ---- AI editor (docs/api.md "Editor", packages/shared/src/editor.ts) --------

const editorPath = (storyId: string) => `${storyPath(storyId)}/editor`;

export const editorApi = {
  thread: (storyId: string) => api.get<EditorThreadResponse>(editorPath(storyId)),
  reset: (storyId: string) => api.post<EditorThreadResponse>(`${editorPath(storyId)}/reset`),
  send: (
    storyId: string,
    content: string,
    selection: EditorSelection | null,
    onEvent: (event: EditorStreamEvent) => void,
    signal?: AbortSignal,
  ) => streamPost<EditorStreamEvent>(`${editorPath(storyId)}/messages`, { content, selection }, onEvent, signal),
};

// ---- uploads -----------------------------------------------------------------

export const uploadsApi = {
  /** Uploads one image for a story. XHR so upload progress can be reported. */
  image: (storyId: string, file: File, onProgress?: (fraction: number) => void) =>
    new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `/api/stories/${storyId}/uploads`);
      xhr.withCredentials = true;
      xhr.responseType = "text";
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable && e.total > 0) onProgress(Math.min(1, e.loaded / e.total));
      };
      xhr.onerror = () => reject(new Error("Upload failed. Check your connection and try again."));
      xhr.onabort = () => reject(new Error("Upload cancelled."));
      xhr.onload = () => {
        let data: unknown = null;
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data as UploadResponse);
          return;
        }
        const d = (data ?? {}) as { error?: string; issues?: { path: string; message: string }[] };
        reject(new ApiError(xhr.status, d.error ?? `Upload failed (${xhr.status})`, d.issues));
      };
      const body = new FormData();
      body.append("file", file, file.name);
      xhr.send(body);
    }),
};

// ---- Learning (docs/api.md "Learning", packages/shared/src/learning.ts) ---

const vocabPath = (storyId: string, chapterId: string) => `${chapterPath(storyId, chapterId)}/vocab`;
const targetPath = (storyId: string, chapterId: string) => `${chapterPath(storyId, chapterId)}/target-words`;

export const vocabApi = {
  list: (storyId: string, chapterId: string) => api.get<VocabWordsResponse>(vocabPath(storyId, chapterId)),
  create: (storyId: string, chapterId: string, body: CreateVocabWordBody) =>
    api.post<VocabWordResponse>(vocabPath(storyId, chapterId), body),
  update: (storyId: string, chapterId: string, id: string, body: UpdateVocabWordBody) =>
    api.patch<VocabWordResponse>(`${vocabPath(storyId, chapterId)}/${id}`, body),
  remove: (storyId: string, chapterId: string, id: string) =>
    api.delete<void>(`${vocabPath(storyId, chapterId)}/${id}`),
};

export const targetWordsApi = {
  list: (storyId: string, chapterId: string) => api.get<TargetWordsResponse>(targetPath(storyId, chapterId)),
  create: (storyId: string, chapterId: string, body: CreateTargetWordBody) =>
    api.post<TargetWordResponse>(targetPath(storyId, chapterId), body),
  update: (storyId: string, chapterId: string, id: string, body: UpdateTargetWordBody) =>
    api.patch<TargetWordResponse>(`${targetPath(storyId, chapterId)}/${id}`, body),
  remove: (storyId: string, chapterId: string, id: string) =>
    api.delete<void>(`${targetPath(storyId, chapterId)}/${id}`),
};

export const learningApi = {
  /** Answers a pending vocabulary card in a session. */
  answerCard: (storyId: string, sessionId: string, body: AnswerCardBody) =>
    api.post<AnswerCardResponse>(`${sessionPath(storyId, sessionId)}/cards`, body),
  profile: () => api.get<ProfileResponse>("/api/profile"),
};
